import React, { createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PermissionService from '../Services/PermissionService';
import { routeFeatureCodes } from '../Static/FeatureMapping';

const PermissionContext = createContext();

/**
 * Permission Provider Component
 * Wraps the app to provide RBAC permission context
 */
export const PermissionProvider = ({ children }) => {
  const permissionsMap = useSelector(state => state.UserPermissions?.permissionsMap || {});

  // Update PermissionService whenever Redux state changes
  // Guard against overwriting with empty state on initial render
  useEffect(() => {
    if (Object.keys(permissionsMap).length > 0) {
      PermissionService.permissionsMap = permissionsMap;
    }
  }, [permissionsMap]);

  /**
   * Check if user has a specific permission
   * @param {string} featureCode - Feature code
   * @param {string} operation - Operation ('view', 'create', 'update', 'delete')
   * @returns {boolean} True if user has permission
   */
  const hasPermission = (featureCode, operation = 'view') => {
    return PermissionService.hasPermission(featureCode, operation);
  };

  /**
   * Check if user has any of the specified permissions
   * @param {string} featureCode - Feature code
   * @param {Array<string>} operations - Array of operations
   * @returns {boolean} True if user has any permission
   */
  const hasAnyPermission = (featureCode, operations = ['view']) => {
    return PermissionService.hasAnyPermission(featureCode, operations);
  };

  /**
   * Get all permissions for a feature
   * @param {string} featureCode - Feature code
   * @returns {Array<string>} Array of operations
   */
  const getFeaturePermissions = (featureCode) => {
    return PermissionService.getFeaturePermissions(featureCode);
  };

  /**
   * Check if user can access a route
   * @param {string} route - Route path
   * @returns {boolean} True if user can access route
   */
  const canAccessRoute = (route) => {
    // V2: Check route permission
    if (PermissionService.isRbacV2Enabled()) {
      const featureCodes = routeFeatureCodes(route);
      if (featureCodes.length === 0) return true; // No restriction
      // Any-of. A route mapped to several features opens on one of them --
      // see the note on '/asset-management' in FeatureMapping.
      return featureCodes.some((code) => hasPermission(code, 'view'));
    }

    // V1: All routes accessible (old system handles it differently)
    return true;
  };

  /**
   * Check if RBAC v2 is enabled
   * @returns {boolean} True if v2 enabled
   */
  const isRbacV2Enabled = () => {
    return PermissionService.isRbacV2Enabled();
  };

  /**
   * Log permission state for debugging
   */
  const logPermissionState = () => {
    PermissionService.logPermissionState();
  };

  const contextValue = {
    hasPermission,
    hasAnyPermission,
    getFeaturePermissions,
    canAccessRoute,
    isRbacV2Enabled,
    logPermissionState,
    permissionsMap
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to use permission context
 * @returns {Object} Permission context
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

export default PermissionContext;
